/**
 * The callout tone -> class/label mapping, kept React-free and dependency-free
 * on purpose. This package has no package.json (see index.ts's doc comment) —
 * it is compiled from source by whichever app imports it, so a test run from
 * inside this package alone cannot resolve `react`. Splitting the plain style
 * data out of `BlockView.tsx` (which does import `react`, for JSX) lets this
 * be unit-tested head-on without a DOM or a React runtime.
 *
 * `BlockView.tsx` pairs each entry here with an icon component to build the
 * full `CALLOUT_TONES` that both `BlockView` (read mode) and `BlockEditor`
 * (edit mode) render from — ONE definition, so the two modes cannot drift the
 * way the merge-field chip once did (see `renderRichText`'s doc comment in
 * `BlockView.tsx` for that history).
 *
 * These pages are LIGHT (`bg-white`/`text-stone-900` — see
 * `applications/super-admin-ui/src/pages/legal/rubyChrome.tsx`), not the dark
 * surface most of this app's chrome renders on, so these are dark text on a
 * light tint rather than the pale-on-dark tints used elsewhere in this
 * package. Every text/background pair is >= 4.5:1 (WCAG AA for body text):
 *   info    blue-900 / blue-50   ~9.5:1
 *   warning amber-900 / amber-50 ~8.8:1
 *   danger  red-900 / red-50     ~9.2:1
 *
 * Colour alone is not how a tone reads: `label` (paired with a per-tone icon
 * in `BlockView.tsx`) is the colour-blind-safe channel.
 */
export type CalloutTone = 'info' | 'warning' | 'danger';

export const CALLOUT_TONE_STYLES: Record<CalloutTone, { className: string; label: string }> = {
  info: { className: 'border-blue-300 bg-blue-50 text-blue-900', label: 'Note' },
  warning: { className: 'border-amber-400 bg-amber-50 text-amber-900', label: 'Warning' },
  danger: { className: 'border-red-400 bg-red-50 text-red-900', label: 'Important' },
};
